import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Instagram, Share2, ArrowLeft, ArrowRight, Play, Pause } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pickArtistField } from "@/data/artists";
import { useArtworkSold } from "@/hooks/useArtworkStatus";
import SoldBadge from "@/components/latir/SoldBadge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---------------- Voice samples (Spanish LatAm, ElevenLabs multilingual v2) ----------------
// These are the 3 candidates offered for LATIR's institutional voice.
// Round 3 — serene contemplative female artist voice (intimate, poetic).
export const VOICE_SAMPLES = [
  { key: "G", label: "Voz G · Nicole", voice_id: "piTKgcLEGmPE4e6mEKli", hint: "Íntima, susurrante, poética" },
  { key: "H", label: "Voz H · Serena", voice_id: "pMsXgVXv3BLzUgSXRplE", hint: "Contemplativa, cálida, artística" },
  { key: "I", label: "Voz I · Emily", voice_id: "LcfcDJNUP1GQjkzn1xUU", hint: "Serena, gentil, femenina" },
];

// A single-instance audio manager so only one player runs at a time.
let currentAudio = null;
function playExclusive(audioEl) {
  if (currentAudio && currentAudio !== audioEl) {
    currentAudio.pause();
  }
  currentAudio = audioEl;
}

// ---------------- price formatter ----------------
export function formatPrice(raw) {
  if (!raw) return null;
  // Match a numeric token: e.g. "1,700", "1700", "500.00", "1.200,50"
  const m = String(raw).match(/[\d][\d.,]*/);
  if (!m) return raw;
  let token = m[0];
  // Strip trailing ",00" or ".00" decimals (art prices are whole dollars)
  token = token.replace(/[.,]\d{1,2}$/, "");
  // Remaining separators are thousands — remove all
  const digits = token.replace(/[.,]/g, "");
  const n = parseInt(digits, 10);
  if (Number.isNaN(n)) return raw;
  return `US$${n.toLocaleString("en-US")}`;
}

// ---------------- audio player primitive ----------------
export function AudioPlayer({ src, label, disabled = false }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onTime = () => setT(a.currentTime);
    const onDur = () => setDur(a.duration || 0);
    const onEnd = () => setPlaying(false);
    const onPause = () => setPlaying(false);
    const onPlay = () => { playExclusive(a); setPlaying(true); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onPause);
    a.addEventListener("play", onPlay);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("play", onPlay);
    };
  }, [src]);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) a.play(); else a.pause();
  };

  const fmt = (s) => {
    if (!s || Number.isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <div className={`flex items-center gap-4 py-3 ${disabled ? "opacity-40" : ""}`}>
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-label={playing ? `Pausar ${label}` : `Reproducir ${label}`}
        data-testid={`play-${label}`}
        className="w-12 h-12 border border-ink flex items-center justify-center hover:bg-ink hover:text-paper transition-colors shrink-0"
      >
        {playing ? <Pause className="w-4 h-4" strokeWidth={1.5} /> : <Play className="w-4 h-4 translate-x-[1px]" strokeWidth={1.5} />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="font-ui text-[0.86rem] text-ink truncate">{label}</p>
        <div className="mt-1.5 h-[2px] bg-line relative">
          <div className="absolute inset-y-0 left-0 bg-latir-red" style={{ width: dur ? `${(t / dur) * 100}%` : "0%" }} />
        </div>
        <div className="mt-1 flex justify-between label-ui text-[0.62rem]">
          <span>{fmt(t)}</span>
          <span>{fmt(dur)}</span>
        </div>
      </div>
      <audio ref={ref} src={src} preload="metadata" />
    </div>
  );
}

// ---------------- accordion primitive ----------------
export function Accordion({ title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-line" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-5 text-left"
        aria-expanded={open}
      >
        <span className="font-ui text-[0.78rem] tracking-[0.22em] uppercase text-ink">{title}</span>
        <ChevronDown className={`w-4 h-4 text-ink-mute transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="pb-8 pr-2">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ---------------- consult form ----------------
function ConsultButton({ artist, sold = false, className = "" }) {
  const { lang } = useLang();
  const isEn = lang === "en";
  const title = pickArtistField(artist, "title", lang) || artist.title;
  const name = artist.name;
  const price = formatPrice(artist.price);
  const hasResolvedPrice = price && price !== "PENDIENTE";
  const url = typeof window !== "undefined" ? window.location.href : "";
  const label = sold
    ? (isEn ? "Enquire about other works by the artist" : "Consultar otras obras del artista")
    : (isEn ? "I'm interested in this work" : "Me interesa esta obra");
  const msg = sold
    ? (isEn
        ? `Hello LATIR, I would like to learn about other available works by ${name}.`
        : `Hola LATIR, me gustaría conocer otras obras disponibles de ${name}.`)
    : (isEn
        ? `Hello, I would like to receive information about the work "${title}" by ${name}, presented at LATIR.` +
          (hasResolvedPrice ? ` Price: ${price}.` : "") +
          `\n\n${url}`
        : `Hola, me interesa recibir información sobre la obra "${title}" de ${name}, presentada en LATIR.` +
          (hasResolvedPrice ? ` Precio: ${price}.` : "") +
          `\n\n${url}`);
  const href = `https://wa.me/50376099209?text=${encodeURIComponent(msg)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid="cta-consult"
      className={`inline-flex items-center justify-center gap-3 bg-ink text-paper px-8 py-4 font-ui text-[0.78rem] tracking-[0.22em] uppercase hover:bg-latir-red transition-colors ${className}`}
    >
      {label} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
    </a>
  );
}

// ---------------- share ----------------
function ShareButton({ artist }) {
  const { lang } = useLang();
  const isEn = lang === "en";
  const title = pickArtistField(artist, "title", lang) || artist.title;
  const [copied, setCopied] = useState(false);
  const doShare = async () => {
    const data = {
      title: `${title} — ${artist.name}`,
      text: isEn ? `${title} by ${artist.name} at LATIR` : `${title} de ${artist.name} en LATIR`,
      url: window.location.href,
    };
    if (navigator.share) {
      try { await navigator.share(data); return; } catch {}
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <button
      type="button"
      onClick={doShare}
      data-testid="cta-share"
      className="inline-flex items-center gap-2 font-ui text-[0.78rem] tracking-[0.22em] uppercase text-ink-soft hover:text-latir-red transition-colors"
    >
      <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />
      {copied
        ? (isEn ? "Link copied" : "Enlace copiado")
        : (isEn ? "Share this work" : "Compartir esta obra")}
    </button>
  );
}

// ---------------- audioguía section ----------------
function AudioGuia({ artist }) {
  const { lang } = useLang();
  const isEn = lang === "en";
  const [tracks, setTracks] = useState({ practice: { ready: false }, inspiration: { ready: false } });

  const practica = pickArtistField(artist, "practica", lang) || artist.practica;
  const inspiracion = pickArtistField(artist, "inspiracion", lang) || artist.inspiracion;
  const bio = pickArtistField(artist, "bio", lang) || artist.bio;
  const title = pickArtistField(artist, "title", lang) || artist.title;

  const refreshStatus = async () => {
    try {
      const r = await fetch(`${BACKEND}/api/tts/status/${artist.slug}?lang=${lang}`);
      const d = await r.json();
      setTracks(d.tracks || {});
    } catch {}
  };

  useEffect(() => { refreshStatus(); }, [artist.slug, lang]);

  return (
    <section className="artwork-audio-section" data-testid="audioguide">
      <p className="font-ui text-[0.72rem] tracking-[0.28em] uppercase text-latir-red">{isEn ? "LATIR Audio Guide" : "Audioguía LATIR"}</p>
      <h2 className="font-display text-[1.7rem] md:text-[2.15rem] leading-[1.05] tracking-[-0.02em] text-ink">{isEn ? "Listen to this work" : "Escuchá esta obra"}</h2>

      {/* Reproductores oficiales */}
      <div className="divide-y divide-line">
        {(tracks.practice?.ready || (practica && practica !== "—")) && (
        <div className="py-2">
          <div className="pt-3 pb-1">
            <p className="font-ui text-[0.72rem] tracking-[0.22em] uppercase text-ink-mute">{isEn ? "Artistic practice" : "Práctica artística"}</p>
          </div>
          {tracks.practice?.ready ? (
            <AudioPlayer src={`${BACKEND}${tracks.practice.audio_url}`} label={isEn ? `Practice of ${artist.name}` : `Práctica de ${artist.name}`} />
          ) : (
            <p className="py-4 label-ui text-ink-mute">{isEn ? "Audio coming soon" : "Audio próximamente"}</p>
          )}
          {practica && practica !== "—" && (
            <Accordion title={isEn ? "Read transcript" : "Leer transcripción"} testid="tr-practice">
              <p className="font-editorial text-[1rem] leading-relaxed text-ink-soft whitespace-pre-line">
                <span className="italic">{isEn ? `Artistic practice of ${artist.name}. ` : `Práctica artística de ${artist.name}. `}</span>
                {practica}
              </p>
            </Accordion>
          )}
        </div>
        )}

        {(tracks.inspiration?.ready || (inspiracion && inspiracion !== "—") || artist.inspirationPending) && (
        <div className="py-2">
          <div className="pt-3 pb-1">
            <p className="font-ui text-[0.72rem] tracking-[0.22em] uppercase text-ink-mute">{isEn ? "Inspiration behind the work" : "Inspiración de la obra"}</p>
          </div>
          {tracks.inspiration?.ready ? (
            <AudioPlayer src={`${BACKEND}${tracks.inspiration.audio_url}`} label={isEn ? `Inspiration of ${title}` : `Inspiración de ${title}`} />
          ) : (
            <p className="py-4 label-ui text-ink-mute">{isEn ? "Audio coming soon" : "Audio próximamente"}</p>
          )}
          {inspiracion && inspiracion !== "—" && (
            <Accordion title={isEn ? "Read transcript" : "Leer transcripción"} testid="tr-inspiration">
              <p className="font-editorial text-[1rem] leading-relaxed text-ink-soft whitespace-pre-line">
                <span className="italic">{isEn ? `About the work ${title}, by ${artist.name}. ` : `Sobre la obra ${title}, de ${artist.name}. `}</span>
                {inspiracion}
              </p>
            </Accordion>
          )}
        </div>
        )}

        {bio && (
          <div className="py-2">
            <div className="pt-3 pb-1">
              <p className="font-ui text-[0.72rem] tracking-[0.22em] uppercase text-ink-mute">{isEn ? "Artist biography" : "Biografía del artista"}</p>
            </div>
            <Accordion title={isEn ? "Read biography" : "Leer biografía"} testid="tr-bio">
              <p className="font-editorial text-[1rem] leading-relaxed text-ink-soft whitespace-pre-line">
                {bio}
              </p>
            </Accordion>
          </div>
        )}
      </div>

      <p className="label-ui text-ink-mute mt-6">
        {isEn
          ? "Narration digitally generated from the texts provided for LATIR."
          : "Narración generada digitalmente a partir de los textos proporcionados para LATIR."}
      </p>
    </section>
  );
}

// ---------------- main artwork detail (redesigned) ----------------
export default function ArtworkDetail({ artist, prev, next, discipline, num, totalCount = 46 }) {
  const { lang } = useLang();
  const dynSold = useArtworkSold(artist.slug);
  const sold = dynSold || artist.status === "vendida";
  const priceStr = formatPrice(artist.price);
  const title = pickArtistField(artist, "title", lang) || artist.title;
  const tecnica = pickArtistField(artist, "tecnica", lang) || artist.tecnica;
  const practica = pickArtistField(artist, "practica", lang) || artist.practica;
  const inspiracion = pickArtistField(artist, "inspiracion", lang) || artist.inspiracion;
  const prevTitle = pickArtistField(prev, "title", lang) || prev.title;
  const nextTitle = pickArtistField(next, "title", lang) || next.title;
  const alt = `${title}, de ${artist.name}`;

  const isEn = lang === "en";
  const statusLabel = sold ? (isEn ? "Sold" : "Vendida")
    : artist.status === "reservada" ? (isEn ? "Reserved" : "Reservada")
    : (isEn ? "Available" : "Disponible");

  const artworkForAudio = { ...artist, title, practica, inspiracion };

  return (
    <div className="artwork-detail-content pb-40 md:pb-24" data-testid="page-artwork-detail">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">

        {/* discreet breadcrumb */}
        <div className="flex items-center justify-between mb-8 md:mb-10">
          <Link to="/artistas" data-testid="detail-back" className="inline-flex items-center gap-2 label-ui hover:text-latir-red transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> {isEn ? "All works" : "Todas las obras"}
          </Link>
          <span className="label-ui text-ink-mute/70">{num} / {String(totalCount).padStart(2, "0")}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">

          {/* IMAGE / VIDEO — left column */}
          <div className="lg:col-span-7">
            <div className="lg:sticky lg:top-24">
              {artist.video ? (
                <div className="artwork-detail-image-container w-full" data-testid="artwork-video">
                  <video
                    src={artist.video}
                    poster={artist.image && !artist.image.includes("placeholder") ? artist.image : undefined}
                    controls
                    preload="metadata"
                    playsInline
                    className="block w-full h-auto object-contain object-center bg-black"
                  />
                </div>
              ) : artist.videoPending ? (
                <div
                  className="artwork-image-placeholder w-full flex items-center justify-center min-h-[240px] max-h-[320px] md:min-h-[320px] md:max-h-[420px] bg-[#f4f1eb]"
                  data-testid="artwork-video-placeholder"
                >
                  <p className="label-ui text-ink-mute">{isEn ? "Video coming soon" : "Video próximamente"}</p>
                </div>
              ) : artist.image && !artist.image.includes("placeholder") ? (
                <div className="artwork-detail-image-container w-full" data-testid="artwork-image">
                  <img
                    src={artist.image}
                    alt={alt}
                    className="block w-full h-auto object-contain object-center"
                  />
                </div>
              ) : (
                <div
                  className="artwork-image-placeholder w-full flex items-center justify-center min-h-[240px] max-h-[320px] md:min-h-[320px] md:max-h-[420px] bg-[#f4f1eb]"
                  data-testid="artwork-image-placeholder"
                >
                  <p className="label-ui text-ink-mute">{isEn ? "Image coming soon" : "Imagen próximamente"}</p>
                </div>
              )}
              <p className="artwork-image-caption label-ui text-ink-mute text-right">
                {title} — {artist.name}
              </p>
            </div>
          </div>

          {/* META — right column */}
          <div className="lg:col-span-5 artwork-heading">
            <p className="artwork-category" data-testid="artwork-discipline">{discipline}</p>
            <h1 className="artwork-title" data-testid="artwork-title">
              {title}
            </h1>
            <p className="artwork-artist-name" data-testid="artwork-artist">
              {artist.name}
            </p>
            {artist.cocreadora && (
              <p
                className="mt-1 font-editorial italic text-[0.95rem] leading-snug text-ink-soft max-w-md"
                data-testid="artwork-cocreator"
              >
                {artist.cocreadora}
              </p>
            )}
            {artist.nacionalidad && (
              <p className="label-ui text-ink-mute mt-2" data-testid="artwork-nationality">
                {artist.nacionalidad}
              </p>
            )}

            {/* year · technique · dimensions */}
            {(() => {
              const medidasVal = pickArtistField(artist, "medidas", lang) || artist.medidas;
              const parts = [artist.year, tecnica, medidasVal].filter((x) => x && x !== "—");
              const isAudiovisual = Boolean(artist.duracion || artist.formato || artist.audiovisual);
              // For audiovisual works with only a year (no tecnica/medidas), render "Año: X" explicitly.
              if (isAudiovisual && parts.length === 1 && artist.year && !tecnica && !medidasVal) {
                return (
                  <p className="artwork-technical-line" data-testid="artwork-facts">
                    <span className="label-ui text-ink-mute mr-2">
                      {isEn ? "Year" : "Año"}:
                    </span>
                    {artist.year}
                  </p>
                );
              }
              return (
                <p className="artwork-technical-line" data-testid="artwork-facts">
                  {parts.join(" · ")}
                </p>
              );
            })()}

            {/* audiovisual (only when explicitly set on the artwork) */}
            {artist.audiovisual && (
              <p
                className="artwork-technical-line mt-1 text-ink-soft"
                data-testid="artwork-audiovisual"
              >
                <span className="label-ui text-ink-mute mr-2">
                  {isEn ? "Audiovisual" : "Audiovisual"}:
                </span>
                {artist.audiovisual}
              </p>
            )}

            {/* duration (for video / audiovisual works) */}
            {artist.duracion && (
              <p
                className="artwork-technical-line mt-1 text-ink-soft"
                data-testid="artwork-duration"
              >
                <span className="label-ui text-ink-mute mr-2">
                  {isEn ? "Duration" : "Duración"}:
                </span>
                {artist.duracion}
              </p>
            )}

            {/* format (for video / audiovisual works) */}
            {artist.formato && (
              <p
                className="artwork-technical-line mt-1 text-ink-soft"
                data-testid="artwork-format"
              >
                <span className="label-ui text-ink-mute mr-2">
                  {isEn ? "Format" : "Formato"}:
                </span>
                {artist.formato}
              </p>
            )}

            {/* edition (only when explicitly set on the artwork) */}
            {artist.edicion && (
              <p
                className="artwork-technical-line mt-1 text-ink-soft"
                data-testid="artwork-edition"
              >
                {artist.edicion === "PENDIENTE" ? (
                  <>
                    <span className="label-ui text-ink-mute mr-2">
                      {isEn ? "Edition" : "Edición"}:
                    </span>
                    {isEn ? "TBD" : "PENDIENTE"}
                  </>
                ) : (
                  pickArtistField(artist, "edicion", lang) || artist.edicion
                )}
              </p>
            )}

            {/* price + status */}
            <div className="artwork-commercial-info">
              {priceStr && priceStr !== "PENDIENTE" && (
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="artwork-price" data-testid="artwork-price">
                    {priceStr}
                  </p>
                  {sold && <SoldBadge testId="artwork-price-sold-badge" />}
                </div>
              )}
              {priceStr === "PENDIENTE" && (
                <p
                  className="artwork-technical-line mt-1 text-ink-soft"
                  data-testid="artwork-price-pending"
                >
                  <span className="label-ui text-ink-mute mr-2">
                    {isEn ? "Price" : "Precio"}:
                  </span>
                  {isEn ? "TBD" : "PENDIENTE"}
                </p>
              )}
              {priceStr && !sold && priceStr !== "PENDIENTE" && (
                <p
                  className="mt-2 font-ui text-[0.72rem] leading-relaxed tracking-[0.02em] text-ink-mute max-w-sm"
                  data-testid="artwork-shipping-note"
                >
                  {isEn
                    ? "The artwork's price does not include shipping. Shipping costs will be calculated based on destination."
                    : "El precio de la obra no incluye gastos de envío. Los costos de envío se calcularán según el destino."}
                </p>
              )}
              {artist.adquisicion && (() => {
                const adqEn = pickArtistField(artist, "adquisicion", lang);
                const adq = (isEn && adqEn && typeof adqEn === "object") ? adqEn : artist.adquisicion;
                return (
                <div
                  className="mt-6 pt-5 border-t border-line/70 max-w-md"
                  data-testid="artwork-acquisition"
                >
                  <p className="label-ui text-ink-mute mb-3">
                    {isEn ? "Acquisition information" : "Información de adquisición"}
                  </p>
                  {adq.intro && (
                    <p className="font-ui text-[0.82rem] leading-relaxed text-ink-soft">
                      {adq.intro}
                    </p>
                  )}
                  {Array.isArray(adq.items) && adq.items.length > 0 && (
                    <ul className="mt-2 space-y-1 font-ui text-[0.82rem] leading-relaxed text-ink-soft list-none">
                      {adq.items.map((item, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span aria-hidden className="text-latir-red">·</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {Array.isArray(adq.notes) &&
                    adq.notes.map((note, idx) => (
                      <p
                        key={idx}
                        className="mt-3 font-ui text-[0.75rem] leading-relaxed text-ink-mute"
                      >
                        {note}
                      </p>
                    ))}
                </div>
                );
              })()}
              <p className={`artwork-availability label-ui ${sold ? "text-ink-mute" : "text-latir-red"}`} data-testid="artwork-status">
                <span className="inline-block w-3 h-px bg-current align-middle mr-2" />
                {statusLabel}
              </p>
              <p className="mt-5 font-editorial italic text-[0.98rem] leading-relaxed text-ink-soft max-w-md">
                {isEn
                  ? "The acquisition of this work supports Fundarosa's early detection programs."
                  : "La adquisición de esta obra apoya los programas de detección temprana de Fundarosa."}
              </p>
              <div className="artwork-actions">
                <ConsultButton artist={artworkForAudio} sold={sold} className="w-full sm:w-auto" />
              </div>
            </div>

            {/* Ficha técnica, "Sobre la práctica" y "Sobre esta obra" removidos de la vista pública.
                Los textos originales se conservan en la base y se leen dentro de las transcripciones
                de la Audioguía LATIR más abajo. */}
            <div className="artwork-social-actions">
              {artist.instagram && (
                <a
                  href={`https://www.instagram.com/${artist.instagram}/`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="detail-instagram"
                  className="inline-flex items-center gap-2 font-ui text-[0.78rem] tracking-[0.22em] uppercase text-ink-soft hover:text-latir-red transition-colors"
                >
                  <Instagram className="w-3.5 h-3.5" strokeWidth={1.5} /> @{artist.instagram} ↗
                </a>
              )}
              {artist.instagram2 && (
                <a
                  href={`https://www.instagram.com/${artist.instagram2}/`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="detail-instagram-2"
                  className="inline-flex items-center gap-2 font-ui text-[0.78rem] tracking-[0.22em] uppercase text-ink-soft hover:text-latir-red transition-colors"
                >
                  <Instagram className="w-3.5 h-3.5" strokeWidth={1.5} /> @{artist.instagram2} ↗
                </a>
              )}
              <ShareButton artist={artworkForAudio} />
            </div>
          </div>
        </div>

        {/* AUDIOGUIDE — full width below */}
        <AudioGuia artist={artworkForAudio} />

        {/* SECOND CTA */}
        <div className="mt-14 md:mt-16 flex flex-col items-center gap-4">
          <ConsultButton artist={artworkForAudio} sold={sold} />
          <Link to="/artistas" className="label-ui text-ink-mute hover:text-latir-red transition-colors mt-2">
            ← {isEn ? "Back to all works" : "Volver a todas las obras"}
          </Link>
        </div>

        {/* PREV / NEXT */}
        <div className="mt-16 pt-8 border-t border-line grid grid-cols-2 gap-6">
          <Link to={`/artistas/${prev.slug}`} data-testid="detail-prev" className="group text-left">
            <span className="label-ui inline-flex items-center gap-2 group-hover:text-latir-red transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> {isEn ? "Previous" : "Obra anterior"}
            </span>
            <p className="font-display text-[1.35rem] md:text-[1.7rem] text-ink mt-3 group-hover:text-latir-red transition-colors">
              {prevTitle}
            </p>
            <p className="font-editorial italic text-[0.95rem] text-ink-soft mt-1">
              {prev.name}
            </p>
          </Link>
          <Link to={`/artistas/${next.slug}`} data-testid="detail-next" className="group text-right">
            <span className="label-ui inline-flex items-center gap-2 justify-end w-full group-hover:text-latir-red transition-colors">
              {isEn ? "Next" : "Siguiente obra"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </span>
            <p className="font-display text-[1.35rem] md:text-[1.7rem] text-ink mt-3 group-hover:text-latir-red transition-colors">
              {nextTitle}
            </p>
            <p className="font-editorial italic text-[0.95rem] text-ink-soft mt-1">
              {next.name}
            </p>
          </Link>
        </div>
      </div>

      {/* MOBILE STICKY BAR */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-line bg-paper/95 backdrop-blur px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-4" data-testid="mobile-sticky-bar">
        <div className="flex-1 min-w-0">
          {priceStr && priceStr !== "PENDIENTE" && <p className="font-display text-[1.15rem] leading-none text-ink truncate">{priceStr}</p>}
          {priceStr === "PENDIENTE" && (
            <p className="font-ui text-[0.72rem] tracking-[0.14em] uppercase text-ink-mute">
              {isEn ? "Price TBD" : "Precio pendiente"}
            </p>
          )}
          <p className={`label-ui mt-1 ${sold ? "text-ink-mute" : "text-latir-red"}`}>{statusLabel}</p>
        </div>
        <ConsultButton artist={artworkForAudio} sold={sold} className="!px-5 !py-3 !text-[0.68rem]" />
      </div>
    </div>
  );
}
